import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import './WarehousePicker.css';

/**
 * Anbar kataloqundan məhsul seçimi (tələbnamə üçün).
 *
 * Seçim məcburi deyil — kataloqda olmayan məhsulu sərbəst yazmaq olar,
 * onda sadəcə mətn saxlanılır və kod/vahid toxunulmaz qalır.
 *
 * Açılan siyahı document.body-yə portal ilə qoyulur və position:fixed işlədir:
 * məhsul cədvəli (overflow-x) və modal (overflow-y) onu başqa cür kəsərdi.
 *
 * props:
 *   value     — cari mətn (item_description)
 *   items     — kataloq [{ id, item_code, name, uom, closing_qty, location }]
 *   onChange  — sərbəst mətn dəyişəndə (text)
 *   onPick    — kataloqdan seçiləndə (item)
 */

const DROP_W = 420;   // açılan siyahının arzu olunan eni
const DROP_MAX_H = 290;

export default function WarehousePicker({ value, items = [], onChange, onPick, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const dropRef = useRef(null);

  const matches = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    if (!q) return items.slice(0, 50);
    const hit = items.filter(it =>
      (it.name || '').toLowerCase().includes(q) ||
      (it.item_code || '').toLowerCase().includes(q)
    );
    // Adı axtarışla başlayanlar yuxarıda olsun
    hit.sort((a, b) => {
      const as = (a.name || '').toLowerCase().startsWith(q) ? 0 : 1;
      const bs = (b.name || '').toLowerCase().startsWith(q) ? 0 : 1;
      return as - bs;
    });
    return hit.slice(0, 50);
  }, [value, items]);

  // Açılan siyahının ekrandakı yerini hesablayır — yer çatmasa yuxarı açılır
  const reposition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    const spaceBelow = vh - r.bottom - 8;
    const spaceAbove = r.top - 8;
    const flipUp = spaceBelow < 160 && spaceAbove > spaceBelow;
    const maxH = Math.max(120, Math.min(DROP_MAX_H, flipUp ? spaceAbove : spaceBelow));

    const width = Math.min(Math.max(r.width, DROP_W), vw - 16);
    let left = r.left;
    if (left + width > vw - 8) left = Math.max(8, vw - width - 8);

    setPos({
      left,
      width,
      maxH,
      top: flipUp ? undefined : r.bottom + 4,
      bottom: flipUp ? vh - r.top + 4 : undefined,
    });
  }, []);

  useEffect(() => {
    if (!open) { setPos(null); return; }
    reposition();
    // capture:true — modal və cədvəl kimi daxili scroll-lar da tutulsun
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  // Siyahı uzunluğu dəyişəndə hündürlük də dəyişir
  useEffect(() => { if (open) reposition(); }, [matches.length, open, reposition]);

  // Kənara klikləyəndə bağlansın (siyahı portal-da olduğu üçün ayrıca yoxlanılır)
  useEffect(() => {
    const onDocDown = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  useEffect(() => { setHighlight(0); }, [value]);

  // Seçili sətir görünən qalsın
  useEffect(() => {
    if (!open || !dropRef.current) return;
    const el = dropRef.current.querySelector('[data-hl="1"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open, pos]);

  const choose = (it) => {
    onPick(it);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { setOpen(true); return; }
    if (!open) return;
    if (e.key === 'ArrowDown')    { e.preventDefault(); setHighlight(h => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter')   { if (matches[highlight]) { e.preventDefault(); choose(matches[highlight]); } }
    else if (e.key === 'Escape')  { setOpen(false); }
  };

  // Cari mətn kataloqdakı məhsulla tam üst-üstə düşürmü
  const picked = useMemo(
    () => items.find(it => (it.name || '').toLowerCase() === (value || '').trim().toLowerCase()),
    [items, value]
  );

  const dropdown = open && pos && (
    <div
      ref={dropRef}
      className="wh-dropdown"
      style={{
        left: pos.left,
        width: pos.width,
        maxHeight: pos.maxH,
        ...(pos.top !== undefined ? { top: pos.top } : { bottom: pos.bottom }),
      }}
    >
      {matches.length === 0 ? (
        <div className="wh-empty">Anbarda tapılmadı — sərbəst yaza bilərsiniz</div>
      ) : (
        matches.map((it, i) => (
          <button
            type="button"
            key={it.id}
            data-hl={i === highlight ? '1' : '0'}
            className={'wh-opt' + (i === highlight ? ' hl' : '')}
            onMouseEnter={() => setHighlight(i)}
            onClick={() => choose(it)}
          >
            <span className="wh-opt-main">
              <span className="wh-opt-name">{it.name}</span>
              <span className="wh-opt-meta">
                {it.item_code}{it.location ? ' · ' + it.location : ''}
              </span>
            </span>
            <span className={'wh-opt-qty' + (it.closing_qty > 0 ? '' : ' zero')}>
              {it.closing_qty} <small>{it.uom}</small>
            </span>
          </button>
        ))
      )}
    </div>
  );

  return (
    <div className="wh-picker" ref={wrapRef}>
      <div className="wh-input-wrap">
        <input
          className={'wh-input' + (picked ? ' is-picked' : '')}
          value={value || ''}
          disabled={disabled}
          onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          required
        />
        {picked && (
          <span className="wh-stock-tag" title={`Anbar qalığı: ${picked.closing_qty} ${picked.uom}`}>
            {picked.closing_qty}
          </span>
        )}
        <button
          type="button"
          className="wh-toggle"
          tabIndex={-1}
          disabled={disabled}
          onClick={() => setOpen(o => !o)}
          aria-label="Anbar siyahısı"
        >
          <Icon name="search" size={13} />
        </button>
      </div>

      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}
